import { FunctionComponent } from "react";

import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { EmailMode } from "./senderSelector.types";

type SelectableMode = Exclude<EmailMode, "custom">;

interface SwitchOption {
	mode: SelectableMode;
	label: string;
	count?: number;
	disabled?: boolean;
}

/**
 * Primary / Secondary / Both segmented control used both as the per-row email selector and as the
 * master bulk control. It is a convenience over the individual inbox checkboxes — picking a mode
 * bulk-sets which inboxes are active, but the user can still de-select any single inbox afterwards
 * (PREC-3564: the primary is freely de-selectable regardless of the mode).
 */
export const EmailModeSwitch: FunctionComponent<{
	mode: EmailMode;
	onChange: (mode: SelectableMode) => void;
	counts?: { primary: number; secondary: number; both: number };
	/** Primary-only sender (no secondaries): the Secondary / Both options are unavailable. */
	disableSecondary?: boolean;
	disabled?: boolean;
	size?: "sm" | "md";
}> = ({ mode, onChange, counts, disableSecondary = false, disabled = false, size = "md" }) => {
	const options: SwitchOption[] = [
		{ mode: "primary", label: "Primary", count: counts?.primary },
		{ mode: "secondary", label: "Secondary", count: counts?.secondary, disabled: disableSecondary },
		{ mode: "both", label: "Both", count: counts?.both, disabled: disableSecondary },
	];

	return (
		<div
			className={classNames(
				"inline-flex gap-0.5 rounded-lg bg-status-info-light p-0.5",
				disabled && "opacity-50"
			)}
		>
			{options.map((option) => {
				const active = mode === option.mode;
				const isDisabled = disabled || option.disabled;

				return (
					<button
						key={option.mode}
						className={classNames(
							"inline-flex items-center gap-1 whitespace-nowrap rounded-md font-semibold transition-colors",
							size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-xs",
							active ? "bg-white text-brand-light shadow-sm" : "text-text-meta",
							isDisabled ? "cursor-not-allowed" : "cursor-pointer hover:text-gray-900"
						)}
						disabled={isDisabled}
						type="button"
						onClick={() => !isDisabled && onChange(option.mode)}
					>
						{active && <Icon className="h-3 w-3" icon={faCheck} />}
						{option.label}
						{typeof option.count === "number" && (
							<span className={active ? "text-brand-light" : "text-text-meta/70"}>
								({option.count})
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
};
